import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Palette, PenTool, Package, Heart } from 'lucide-react';

const steps = [
  {
    icon: Palette,
    title: 'Choose Your Design',
    description: 'Select from our curated collection or bring your own vision',
    step: '01',
  },
  {
    icon: PenTool,
    title: 'Personalise',
    description: 'Add your special duas, names, or meaningful text in Urdu, Arabic, or English',
    step: '02',
  },
  {
    icon: Heart,
    title: 'Handcrafted with Love',
    description: 'Our artisans bring your vision to life with meticulous care',
    step: '03',
  },
  {
    icon: Package,
    title: 'Delivered with Barakah',
    description: 'Your precious order arrives beautifully packaged at your doorstep',
    step: '04',
  },
];

const HowItWorks = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-background relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-primary-light/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-silver/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm text-silver font-sans tracking-wider uppercase mb-2 block">
            Our Process
          </span>
          <h2 className="font-display text-2xl md:text-3xl text-foreground mb-4">
            How It Works
          </h2>
          <p className="text-muted-foreground font-body max-w-xl mx-auto">
            Your journey to a beautifully personalised creation
          </p>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative group"
              >
                {/* Connecting Line (hidden on mobile and last item) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-1/2 w-full h-px bg-gradient-to-r from-border via-silver/30 to-border" />
                )}

                <div className="relative bg-gradient-card border border-border rounded-2xl p-6 h-full hover:border-silver/30 transition-all duration-300 hover-glow">
                  {/* Step Number */}
                  <div className="absolute -top-3 -right-2 bg-primary-light text-ivory text-xs font-medium px-2.5 py-1 rounded-full">
                    {step.step}
                  </div>

                  {/* Icon */}
                  <div className="h-14 w-14 rounded-xl bg-muted/50 flex items-center justify-center mb-5 group-hover:bg-silver/10 transition-colors">
                    <Icon className="h-6 w-6 text-silver group-hover:text-silver-light transition-colors" />
                  </div>

                  {/* Content */}
                  <h3 className="font-display text-lg text-silver-light mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
